import * as React from "react";
import { useFormContext } from "react-hook-form";

import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../primitives/form";
import { Input } from "../primitives/input";
import {
  type AmountSeparators,
  amountEditIntent,
  caretAfterSignificant,
  countSignificantBefore,
  formatGroupedAmount,
  parseGroupedAmount,
  separatorsFor,
} from "./amountInputFormat";

/**
 * Which characters the grouped display uses — the reader's, like every other amount on screen.
 *
 * It was pinned to `es-CO` on the premise that the numbers this field sits next to all render through
 * `formatDecimalAmount2`, which defaults to es-CO. Half of them do; the one physically closest does not. The
 * quote breakdown is `ColocaAmountDisplay`, which resolves `locale ?? activeLanguage` — so to an English
 * reader the field wrote `1.500,50` directly above a breakdown reading `1,500.50`. **The separators came out
 * inverted**, which on this screen is not a cosmetic disagreement: `1.500` is fifteen hundred under one
 * convention and one and a half under the other, the exact ambiguity the parser below spends six rules
 * resolving.
 *
 * Note this decides characters and nothing else. `formatGroupedAmount` groups every three digits with a
 * regular expression, so the field always groups whatever the locale's own minimum would suppress — `es`
 * leaves four-digit numbers unseparated per the RAE, and the field does not. That is deliberate and it is why
 * `ColocaAmountDisplay` and `formatDecimalAmount2` now both ask Intl for `useGrouping: "always"`: in a
 * dashboard the grouping is the app's voice rather than the reader's, and a figure must not change shape
 * between the field it is typed in and the line that prices it.
 */
function useGroupingSeparators(): AmountSeparators {
  const { activeLanguage } = useTranslations("global");
  return React.useMemo(() => separatorsFor(activeLanguage), [activeLanguage]);
}

/**
 * Keeps only what a positive decimal can contain, resolving "." and "," deterministically instead of a blind
 * comma→dot replace (which misread pasted grouped amounts: "1,234.56" became "1.23456"). The LAST separator
 * is the decimal point when it is the only occurrence of its kind after every other separator; the rest read
 * as thousands marks and are dropped. A separator kind that repeats is grouping when it cuts exact 3-digit
 * groups ("1.234.567" → "1234567"); anything else is a typing artifact and the first occurrence stays the
 * decimal point ("1.." → "1."). A single typed comma still works as the decimal separator (es/pt keyboards):
 * "1,5" → "1.5". Fraction digits are capped so the value can never exceed what the API accepts, and integer
 * digits are capped at the `numeric(20,8)` capacity.
 */
function sanitizeDecimalInput(raw: string, maxFractionDigits: number): string {
  const [intRaw, fracRaw] = splitDecimal(raw.replace(/[^\d.,]/g, ""));
  const intPart = intRaw.slice(0, 12);
  if (fracRaw == null || maxFractionDigits === 0) return intPart;
  return `${intPart}.${fracRaw.slice(0, maxFractionDigits)}`;
}

/** Integer and fraction digits of the sanitized text; a null fraction means no decimal separator survived. */
function splitDecimal(value: string): [string, string | null] {
  const digitsOnly = (text: string) => text.replace(/[.,]/g, "");
  const hasDot = value.includes(".");
  const hasComma = value.includes(",");

  if (hasDot && hasComma) {
    const lastSeparator = Math.max(value.lastIndexOf("."), value.lastIndexOf(","));
    const kind = value[lastSeparator];
    if (value.indexOf(kind) === lastSeparator) {
      return [digitsOnly(value.slice(0, lastSeparator)), digitsOnly(value.slice(lastSeparator + 1))];
    }
    return [digitsOnly(value), null];
  }

  const separator = hasDot ? "." : hasComma ? "," : null;
  if (separator == null) return [value, null];

  const parts = value.split(separator);
  if (parts.length === 2) return [parts[0], parts[1]];
  const groups = parts.slice(1);
  if (groups.every((group) => group.length === 3)) return [parts.join(""), null];
  return [parts[0], groups.join("")];
}

/** Right padding that clears the suffix, sized from its character count so `%` and `op. currency` both fit. */
function suffixPadding(suffix: React.ReactNode): string | undefined {
  if (typeof suffix !== "string") return undefined;
  return `calc(${suffix.length}ch + 1.25rem)`;
}

export interface ColocaAmountInputControlledProps extends Omit<
  React.ComponentPropsWithoutRef<"input">,
  "value" | "onChange" | "type"
> {
  /**
   * Current value as a plain decimal **string** (e.g. `"1500.00"`). Never a `number` — runtime money carries
   * up to 8 fraction digits and a `Number` round-trip can lose the last one.
   */
  value: string;
  onValueChange: (value: string) => void;
  /**
   * Unit rendered inside the field, right-aligned: a currency code (`"MXN"`), a `%`, or a fallback label when
   * the currency is not yet known (`"op. currency"`). Omit for a bare number.
   */
  suffix?: React.ReactNode;
  /** Fraction digits the user is allowed to type. Defaults to 8 (the runtime money scale). */
  maxFractionDigits?: number;
  /**
   * Show thousands separators while the user types (`1.500,50`). **Display only** — `value` and
   * `onValueChange` keep carrying the plain dot-decimal string, so nothing downstream changes.
   *
   * Off by default, and deliberately so: most call sites of this component are the payments-config price
   * book, where several fields are **percentages** (`suffix="%"`) that must not be grouped. Turn it on for an
   * amount somebody types in money terms — what they send, what a beneficiary receives.
   */
  groupThousands?: boolean;
  /** Extra classes for the wrapper. Use `inputClassName` to reach the `<input>` itself. */
  className?: string;
  inputClassName?: string;
}

/**
 * Standalone (not form-bound) amount input: right-aligned tabular monospace digits with an optional unit
 * suffix inside the field. Use for amount _lenses_ that are not part of a submitted form — a reference amount
 * that drives a preview, a filter threshold, or a row inside a repeater.
 *
 * The value is a decimal string end to end. Pair it with the helpers in `@/shared/utils/decimal` for any
 * arithmetic, and with `ColocaAmountDisplay` for read-only rendering.
 *
 * @summary decimal-string amount input with a unit suffix, for non-form contexts
 */
export const ColocaAmountInputControlled = React.forwardRef<
  HTMLInputElement,
  ColocaAmountInputControlledProps
>(function ColocaAmountInputControlled(
  {
    value,
    onValueChange,
    suffix,
    maxFractionDigits = 8,
    groupThousands = false,
    className,
    inputClassName,
    style,
    ...rest
  },
  ref,
) {
  const separators = useGroupingSeparators();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  // Where the caret must land once React has painted the reformatted text, measured in digits rather than
  // characters. Null on every render the user did not cause, so programmatic value changes (a quote filling
  // the other side of the form) never steal the caret.
  const pendingCaret = React.useRef<number | null>(null);

  const setRefs = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  const displayed = groupThousands ? formatGroupedAmount(value, separators) : value;

  // Puts the caret back where the digits say it belongs, and clears the request. Whoever gets here first
  // wins; the other call is a no-op. Reads the live `value` off the node rather than the rendered
  // `displayed`, so it is correct on both paths below — after a commit the two are the same string.
  const applyPendingCaret = React.useCallback(() => {
    const caret = pendingCaret.current;
    pendingCaret.current = null;
    const node = inputRef.current;
    // Never move a caret the user is not sitting on: a programmatic write (a quote filling the field) must
    // not inherit the position of an edit that never rendered.
    if (caret == null || !node || document.activeElement !== node) return;
    const offset = caretAfterSignificant(node.value, caret, separators);
    node.setSelectionRange(offset, offset);
  }, [separators]);

  // Layout effect, not effect: the caret has to be restored in the same frame the new text paints, or it is
  // visibly thrown to the end of the field on every keystroke.
  //
  // No dependency array, on purpose: the pending caret must be CONSUMED by the very next paint, whatever
  // caused it. Keyed on `displayed` it survived a render where the text did not change — a rejected
  // character, which parses back to the value already held — and was then applied to the next render that
  // did change it, which may well be a quote filling the field rather than anything the user did. Reading a
  // ref on every render is cheaper than that class of bug.
  React.useLayoutEffect(() => {
    applyPendingCaret();
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (!groupThousands) {
      onValueChange(sanitizeDecimalInput(raw, maxFractionDigits));
      return;
    }

    const caret = event.target.selectionStart ?? raw.length;
    // What the edit was, which the text alone cannot say — the decision is `amountEditIntent`'s, so it is
    // tested without a DOM. All this does is hand over what the browser reported.
    const native = event.nativeEvent as InputEvent;
    const intent = amountEditIntent(
      {
        inputType: typeof native?.inputType === "string" ? native.inputType : "",
        data: typeof native?.data === "string" ? native.data : null,
        raw,
        displayed,
        caret,
      },
      separators,
    );

    pendingCaret.current = countSignificantBefore(raw, caret, separators);
    onValueChange(parseGroupedAmount(raw, separators, maxFractionDigits, intent));

    // A rejected character — a letter, a decimal past the cap, a second separator — parses back to the value
    // already held, so there is no render and the layout effect above never runs. React still repairs the
    // field: `batchedUpdates` calls `restoreStateOfTarget` after the flush, which reassigns `node.value` and
    // collapses the selection to the end. This microtask runs after that reassignment and before the paint,
    // which makes it the only point where the caret can be returned without a visible jump. Idempotent: on
    // the ordinary path the layout effect has already taken the ref and this finds nothing to do.
    //
    // It depends on React running `restoreStateOfTarget` before the microtask queue drains, which is an
    // ordering React does not contract. Verified against **react-dom 19.2.1**, in Chromium, by
    // `ColocaAmountInput.stories.tsx` → `GroupedThousands`. If that story starts failing after a React
    // upgrade, this is the line to look at — the caret jumping to the end of the field is the symptom.
    queueMicrotask(applyPendingCaret);
  };

  return (
    <span className={cn("relative block", className)}>
      <Input
        ref={setRefs}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={displayed}
        onChange={handleChange}
        className={cn("text-right font-mono tabular-nums", inputClassName)}
        style={{ paddingInlineEnd: suffixPadding(suffix), ...style }}
        {...rest}
      />
      {suffix != null && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-xs text-muted-foreground"
        >
          {suffix}
        </span>
      )}
    </span>
  );
});

export interface ColocaAmountInputProps extends Omit<
  ColocaAmountInputControlledProps,
  "value" | "onValueChange"
> {
  /** Field name as registered in the parent `useForm` schema. Must match the Zod key exactly. */
  name: string;
  /** Visible label rendered above the input. */
  label?: React.ReactNode;
  /** Helper text below the input — use for the unit, the precision limit, or what the number clamps. */
  description?: React.ReactNode;
}

/**
 * Amount input wired to the nearest React Hook Form context — the canonical way to capture money and
 * percentages anywhere in the dashboard.
 *
 * Stores a plain decimal **string** in form state, so the value the API receives is exactly what the user
 * typed (no `Number` round-trip, no scientific notation, no lost 8th decimal). Digits are right-aligned in
 * tabular monospace so a column of fields lines up, and the unit lives inside the field via `suffix`.
 *
 * Validate with a Zod string schema (`MONEY_INPUT_PATTERN` / `PERCENTAGE_INPUT_PATTERN` from
 * `@/shared/utils/decimal`) rather than `z.number()` — the string is the source of truth.
 *
 * @summary RHF-bound decimal-string amount/percentage input with a unit suffix
 */
export function ColocaAmountInput({ name, label, description, onBlur, ...rest }: ColocaAmountInputProps) {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <ColocaAmountInputControlled
              {...rest}
              name={field.name}
              ref={field.ref}
              value={typeof field.value === "string" ? field.value : ""}
              onValueChange={field.onChange}
              onBlur={(event) => {
                field.onBlur();
                onBlur?.(event);
              }}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
